/**
 * Member Verification tab — Event Member Attribution.
 *
 * A rep/manager opens the event and confirms which membership sign-ups were theirs.
 * The pool is server-scoped (anti-harvest): voucher sign-ups from the event's QR code, plus (mig 1390)
 * members who joined with the event organization's promo code while the event was on — the same matching
 * as Event Performance's "New members (promo code)". Members who matched two overlapping shows of the same
 * organization are listed on both, flagged, until one is verified. Non-managers must be assigned to the event. Managers may credit an assigned rep; a regular
 * rep credits themselves. Attribution-only — no voucher, no attendance/capacity impact.
 *
 * Backend: get_event_signup_pool / verify_/unverify_event_member_signup /
 * get_event_attribution_summary (see eventAttribution.service.ts).
 */

import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getEventSignupPool,
  verifyEventMemberSignup,
  unverifyEventMemberSignup,
  getEventAttributionSummary,
  type SignupPoolRow,
} from "../../../services/eventAttribution.service";
import { getMyCurrentSalesPositionIds } from "../../../services/msrPortal.service";
import { supabase } from "../../../services/supabase";
import { useAuth } from "../../../contexts/AuthContext";
import { useToast } from "../../ui/Toast";
import Button from "../../common/Button";
import type { UUID } from "../../../types/common.types";
import {
  sortSalesPositionsByFirstName,
  displaySalesPositionName,
} from "../../../utils/salesPositionDisplay";
import {
  describeSignupReason,
  formatEventLocalTime,
} from "../../../utils/signupPoolReason";
import { friendlyError } from "../../../utils/memberVerificationErrors";

interface Props {
  eventId: UUID;
}

interface AssignedPosition {
  id: UUID;
  title: string;
  assigned_name: string | null;
  is_active: boolean;
}

/** Format a sign-up date string into a short readable date; falls back to the raw value. */
const formatSignupDate = (s: string | null): string => {
  if (!s) {
    return "—";
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) {
    return s;
  }
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(d);
};

const MemberVerificationTab: React.FC<Props> = ({ eventId }) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const role = (user?.role ?? "").toLowerCase().replace(/_/g, "");
  const isManager =
    role === "manager" ||
    role === "regionalmanager" ||
    role === "territorymanager" ||
    role === "admin" ||
    role === "superadmin";

  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [creditPositionId, setCreditPositionId] = useState<string>(""); // "" = credit to me
  const [pendingContactId, setPendingContactId] = useState<UUID | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["event-signup-pool", eventId] });
    queryClient.invalidateQueries({
      queryKey: ["event-attribution-summary", eventId],
    });
  };

  const pool = useQuery({
    queryKey: ["event-signup-pool", eventId, debounced],
    queryFn: () => getEventSignupPool(eventId, debounced),
    retry: false,
  });

  const summary = useQuery({
    queryKey: ["event-attribution-summary", eventId],
    queryFn: () => getEventAttributionSummary(eventId),
    retry: false,
  });

  // Reps assigned to THIS event — the manager's credited-rep picker list.
  const assignedReps = useQuery({
    queryKey: ["event-assigned-reps", eventId],
    enabled: isManager,
    queryFn: async (): Promise<AssignedPosition[]> => {
      const { data, error } = await supabase
        .from("event_sales_assignments")
        .select("sales_positions(id, title, assigned_name, is_active)")
        .eq("event_id", eventId);
      if (error) {
        throw error;
      }
      const rows = (data ?? [])
        .map(
          (r: { sales_positions: AssignedPosition | null }) =>
            r.sales_positions,
        )
        .filter((p): p is AssignedPosition => !!p && p.is_active);
      return sortSalesPositionsByFirstName(rows) as AssignedPosition[];
    },
  });

  // The caller's own sales position(s) — used to show the Remove button only on rows the
  // current rep actually verified. Managers can always remove, so they don't need this.
  const myPositions = useQuery({
    queryKey: ["my-sales-position-ids"],
    enabled: !isManager,
    queryFn: getMyCurrentSalesPositionIds,
    staleTime: 5 * 60 * 1000,
  });
  const myPositionIds = myPositions.data ?? [];

  const verifyMut = useMutation({
    mutationFn: (contactId: UUID) =>
      verifyEventMemberSignup(
        eventId,
        contactId,
        isManager && creditPositionId ? (creditPositionId as UUID) : null,
      ),
    onMutate: (contactId) => setPendingContactId(contactId),
    onSettled: () => setPendingContactId(null),
    onSuccess: () => {
      showToast("success", "Member sign-up verified");
      invalidate();
    },
    onError: (e: unknown) =>
      showToast("error", friendlyError(e, "Could not verify this sign-up.")),
  });

  const unverifyMut = useMutation({
    mutationFn: (contactId: UUID) =>
      unverifyEventMemberSignup(eventId, contactId),
    onMutate: (contactId) => setPendingContactId(contactId),
    onSettled: () => setPendingContactId(null),
    onSuccess: () => {
      showToast("success", "Verification removed");
      invalidate();
    },
    onError: (e: unknown) =>
      showToast(
        "error",
        friendlyError(e, "Could not remove this verification."),
      ),
  });

  // Graceful permission handling — non-managers not assigned to the event get a 403 from the RPC.
  const poolError = pool.error as Error | null;
  const notAssigned =
    !!poolError &&
    /not assigned to this event|permission_denied/i.test(poolError.message);

  const rows: SignupPoolRow[] = pool.data ?? [];
  const sum = summary.data ?? null;

  return (
    <div className="space-y-5">
      {/* Summary / close-rate */}
      {sum && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Sign-ups in pool" value={String(sum.total_pool)} />
          <Stat label="Verified" value={String(sum.total_verified)} />
          <Stat
            label="Attendance"
            value={
              sum.attendance_count !== null ? String(sum.attendance_count) : "—"
            }
          />
          <Stat
            label="Close rate"
            value={
              sum.close_rate !== null
                ? `${Math.round(sum.close_rate * 100)}%`
                : "—"
            }
          />
        </div>
      )}
      <div
        className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-900"
        data-testid="member-verification-rules"
      >
        <p className="font-medium">Who is listed here</p>
        <ul className="mt-1 list-disc pl-5 space-y-0.5">
          <li>
            Members who got a sign-up voucher from this event&apos;s QR code.
          </li>
          <li>
            Members who joined with this show&apos;s promoter (or host) promo
            code between opening time and midnight after the last day, in the
            show&apos;s own time zone. They may not have checked in.
          </li>
          <li>
            If the same promoter had another show open at that time and we
            can&apos;t tell which one the member came from, they&apos;re listed
            on both and marked <span className="font-medium">Also matched</span>
            . Verify them on the right show and they drop off the other.
          </li>
        </ul>
      </div>

      {sum?.single_rep_auto && (
        <p className="text-sm text-gray-500">
          One rep is assigned — all qualifying sign-ups are attributed to them
          automatically, except members marked “Also matched” another show: they
          count once someone confirms which show they joined at.
        </p>
      )}

      {/* Manager credited-rep picker */}
      {isManager && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <label className="text-sm text-gray-600">
            Credit verifications to:
          </label>
          <select
            className="border rounded-lg px-3 py-2 text-sm max-w-xs"
            value={creditPositionId}
            onChange={(e) => setCreditPositionId(e.target.value)}
          >
            <option value="">Me (the verifier)</option>
            {(assignedReps.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {displaySalesPositionName(p, { markInactive: true })}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Search */}
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search sign-ups by name, email, member #, phone, or promo code"
        className="w-full border rounded-lg px-3 py-2 text-sm"
      />

      {/* States */}
      {notAssigned ? (
        <EmptyNote text="You're not assigned to this event, so its sign-ups aren't visible to you. Ask a manager to add you to the event." />
      ) : pool.isLoading ? (
        <EmptyNote text="Loading sign-ups…" />
      ) : poolError ? (
        <EmptyNote
          text={friendlyError(
            poolError,
            "Couldn't load sign-ups for this event. Please try again.",
          )}
        />
      ) : rows.length === 0 ? (
        <EmptyNote
          text={
            debounced
              ? "No matching sign-ups in this event's pool."
              : "No QR-code or promo-code sign-ups found for this event yet."
          }
        />
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-3 py-2">Member</th>
                <th className="text-left px-3 py-2 hidden md:table-cell">
                  Member #
                </th>
                <th className="text-left px-3 py-2 hidden sm:table-cell">
                  Signed up (show time)
                </th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const busy =
                  pendingContactId === r.contact_id &&
                  (verifyMut.isPending || unverifyMut.isPending);
                // Only the verifier (own position) or a manager may remove — the RPC enforces
                // this too, but hide the button so reps aren't offered an action that will fail.
                const canRemove =
                  isManager ||
                  (r.attributed_sales_position_id !== null &&
                    myPositionIds.includes(r.attributed_sales_position_id));
                let action: React.ReactNode;
                if (!r.already_attributed) {
                  action = (
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={busy}
                      onClick={() => verifyMut.mutate(r.contact_id)}
                    >
                      Verify
                    </Button>
                  );
                } else if (canRemove) {
                  action = (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() => unverifyMut.mutate(r.contact_id)}
                    >
                      Remove
                    </Button>
                  );
                } else {
                  action = <span className="text-gray-300 text-xs">—</span>;
                }
                const reason = describeSignupReason(r);
                const signedUp =
                  formatEventLocalTime(r.signup_at, r.event_timezone) ??
                  formatSignupDate(r.signup_date);
                return (
                  <tr
                    key={r.contact_id}
                    className={`border-t hover:bg-gray-50 ${reason.ambiguity ? "bg-amber-50" : ""}`}
                  >
                    <td className="px-3 py-2">
                      <div className="font-medium">
                        {[r.first_name, r.last_name]
                          .filter(Boolean)
                          .join(" ") || "—"}
                      </div>
                      <div className="text-gray-500 text-xs">
                        {r.email ?? r.phone_number ?? ""}
                      </div>
                      <div
                        className="text-gray-600 text-xs mt-0.5"
                        data-testid="signup-reason"
                      >
                        {reason.text}
                      </div>
                      {reason.ambiguity && (
                        <div
                          className="mt-1 inline-block rounded-md bg-amber-100 text-amber-900 px-2 py-1 text-xs leading-snug"
                          data-testid="signup-ambiguity"
                        >
                          {reason.ambiguity}
                        </div>
                      )}
                      {/* mobile: surface member # (its column is hidden < md) */}
                      {r.membership_id && (
                        <div className="text-gray-400 text-xs md:hidden">
                          Member # {r.membership_id}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 hidden md:table-cell">
                      {r.membership_id ?? "—"}
                    </td>
                    <td className="px-3 py-2 hidden sm:table-cell whitespace-nowrap">
                      {signedUp}
                    </td>
                    <td className="px-3 py-2">
                      {r.already_attributed ? (
                        <span className="inline-flex items-center rounded-full bg-green-100 text-green-800 px-2 py-0.5 text-xs">
                          Verified
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">
                          Unverified
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {action}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-lg border bg-white p-3">
    <div className="text-xs text-gray-500">{label}</div>
    <div className="text-xl font-semibold">{value}</div>
  </div>
);

const EmptyNote: React.FC<{ text: string }> = ({ text }) => (
  <div className="rounded-lg border border-dashed p-6 text-center text-sm text-gray-500">
    {text}
  </div>
);

export default MemberVerificationTab;
